import { useId } from 'react';
import { useTranslation } from 'react-i18next';

// PLATFORM_LANGUAGES is the ordered set of languages the public website can
// render in, with each language's endonym (its own name) — matching the
// backend's sites.PlatformLanguages / LanguageEndonym. Endonyms stay
// recognisable whatever UI locale the admin is using.
export const PLATFORM_LANGUAGES: { code: string; endonym: string }[] = [
  { code: 'sv', endonym: 'Svenska' },
  { code: 'no', endonym: 'Norsk' },
  { code: 'da', endonym: 'Dansk' },
  { code: 'fi', endonym: 'Suomi' },
  { code: 'en', endonym: 'English' },
];

export interface LanguagesFieldProps {
  // The site's selected language codes and which one is primary (served at
  // the bare URL, shown by default). primary is always one of supported.
  supported: string[];
  primary: string;
  onChange: (next: { supported: string[]; primary: string }) => void;
  // When the surrounding surface already carries a "Språk" heading (the
  // design-page settings tab wraps this in a titled DesignSection), the
  // fieldset's own visible legend just repeats it. hideHeading keeps the
  // legend for screen readers but removes it visually. The wizard leaves
  // it on — there the legend IS the section heading.
  hideHeading?: boolean;
}

// LanguagesField lets an admin pick which platform languages a website
// offers and which is primary. Invariants it maintains: at least one
// language is always selected, and the primary is always a selected
// language. Reused by the website creation wizard and the design-page
// settings tab.
export function LanguagesField({ supported, primary, onChange, hideHeading }: LanguagesFieldProps) {
  const { t } = useTranslation('common');
  const radioName = useId();

  const toggle = (code: string) => {
    if (supported.includes(code)) {
      // Removing — keep at least one language, and move primary off it.
      if (supported.length <= 1) return;
      const next = supported.filter((c) => c !== code);
      // next is non-empty here (length was >= 2); ?? satisfies the
      // no-unchecked-index rule without changing behavior.
      const nextPrimary = primary === code ? (next[0] ?? primary) : primary;
      onChange({ supported: next, primary: nextPrimary });
      return;
    }
    // Adding — re-derive the list in canonical platform order.
    const next = PLATFORM_LANGUAGES.filter(
      (l) => l.code === code || supported.includes(l.code),
    ).map((l) => l.code);
    onChange({ supported: next, primary: primary || code });
  };

  const setPrimary = (code: string) => {
    if (!supported.includes(code)) return;
    onChange({ supported, primary: code });
  };

  return (
    <fieldset className="languages-field">
      <legend className={hideHeading ? 'sr-only' : undefined}>
        {t('website.wizard.languages.heading')}
      </legend>
      <p className="languages-field__help">{t('website.wizard.languages.help')}</p>
      <ul className="languages-field__list">
        {PLATFORM_LANGUAGES.map(({ code, endonym }) => {
          const on = supported.includes(code);
          const isPrimary = primary === code;
          return (
            <li key={code} className="languages-field__item">
              <label className="languages-field__toggle">
                <input type="checkbox" checked={on} onChange={() => toggle(code)} />
                <span lang={code}>{endonym}</span>
              </label>
              {on && (
                <label className="languages-field__primary">
                  <input
                    type="radio"
                    name={radioName}
                    checked={isPrimary}
                    onChange={() => setPrimary(code)}
                  />
                  <span>
                    {isPrimary
                      ? t('website.wizard.languages.primary')
                      : t('website.wizard.languages.makePrimary')}
                  </span>
                </label>
              )}
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}
